<script setup lang="ts">
import { computed, onMounted, reactive, ref } from "vue";
import { message } from "ant-design-vue";
import {
  getAdminRegistrationLimitSettings,
  saveAdminRegistrationLimitSettings,
  type AdminRegistrationLimitSettings
} from "../admin-api";

const loading = ref(false);
const saving = ref(false);
const current = reactive({
  china: 0,
  global: 0
});
const form = reactive({
  chinaLimit: 0,
  globalLimit: 0
});

const chinaPercent = computed(() => calculatePercent(current.china, form.chinaLimit));
const globalPercent = computed(() => calculatePercent(current.global, form.globalLimit));

onMounted(loadSettings);

async function loadSettings() {
  loading.value = true;
  try {
    applySettings(await getAdminRegistrationLimitSettings());
  } catch (currentError) {
    message.error(currentError instanceof Error ? currentError.message : "注册人数限制加载失败。");
  } finally {
    loading.value = false;
  }
}

async function submitSettings() {
  saving.value = true;
  try {
    const response = await saveAdminRegistrationLimitSettings({
      chinaLimit: normalizedLimit(form.chinaLimit),
      globalLimit: normalizedLimit(form.globalLimit)
    });
    message.success(response.message);
    await loadSettings();
  } catch (currentError) {
    message.error(currentError instanceof Error ? currentError.message : "注册人数限制保存失败。");
  } finally {
    saving.value = false;
  }
}

function applySettings(settings: AdminRegistrationLimitSettings) {
  current.china = settings.chinaCurrent ?? 0;
  current.global = settings.globalCurrent ?? 0;
  form.chinaLimit = settings.chinaLimit ?? 0;
  form.globalLimit = settings.globalLimit ?? 0;
}

function calculatePercent(count: number, limit: number) {
  if (limit <= 0) {
    return 0;
  }

  return Math.min(100, Math.round((count / limit) * 100));
}

function normalizedLimit(value: number) {
  return Math.max(0, Math.floor(Number(value) || 0));
}
</script>

<template>
  <a-card class="admin-settings-card" :loading="loading">
    <template #title>注册人数限制</template>

    <a-alert
      class="admin-form-alert"
      type="info"
      show-icon
      message="用于控制本轮开放测试人数。设置为 0 表示不限制；达到上限后，仅拦截新用户注册，已注册用户仍可继续登录。"
    />

    <a-form layout="vertical" :model="form" @finish="submitSettings">
      <a-row :gutter="16">
        <a-col :xs="24" :md="12">
          <a-card class="admin-metric-card" size="small">
            <template #title>国内版 CN</template>
            <a-statistic title="当前注册人数" :value="current.china" />
            <a-progress
              :percent="chinaPercent"
              :show-info="form.chinaLimit > 0"
              :status="form.chinaLimit > 0 && current.china >= form.chinaLimit ? 'exception' : 'normal'"
            />
            <a-form-item label="注册人数上限">
              <a-input-number
                v-model:value="form.chinaLimit"
                :min="0"
                :precision="0"
                style="width: 100%"
                placeholder="0 表示不限"
              />
            </a-form-item>
          </a-card>
        </a-col>

        <a-col :xs="24" :md="12">
          <a-card class="admin-metric-card" size="small">
            <template #title>国际版 GLOBAL</template>
            <a-statistic title="当前注册人数" :value="current.global" />
            <a-progress
              :percent="globalPercent"
              :show-info="form.globalLimit > 0"
              :status="form.globalLimit > 0 && current.global >= form.globalLimit ? 'exception' : 'normal'"
            />
            <a-form-item label="注册人数上限">
              <a-input-number
                v-model:value="form.globalLimit"
                :min="0"
                :precision="0"
                style="width: 100%"
                placeholder="0 表示不限"
              />
            </a-form-item>
          </a-card>
        </a-col>
      </a-row>

      <a-space class="admin-form-actions">
        <a-button type="primary" html-type="submit" :loading="saving">保存注册限制</a-button>
        <a-button type="default" @click="loadSettings">刷新人数</a-button>
      </a-space>
    </a-form>
  </a-card>
</template>
